/* eslint-disable max-lines */ // will optimize the code later , dev in progress..

import {
  useState,
  type FC,
  type JSX,
  type ChangeEvent,
  useRef,
  useEffect,
} from 'react';

import {
  Tooltip,
  getStoreValue,
  Icon,
  Typography,
  ChatModalAi,
  MessageBox,
  Prompt,
  checkEmpty,
} from 'pixel-react';
import { useSelector } from 'react-redux';

import { CHATBOT_CONSTANTS } from '@/constants/chatbotConstants';
import { CONFIG } from '@/lib/constants/config';
import { RootState } from '@/store';
import {
  useChatbotMutation,
  useClearChatbotMutation,
  useLazyGetChatbotQuery,
} from '@/store/chatbot/chatbotSlice';
import { Test_Optimize_ResponseObject } from '@/store/commonApiType/testOptimizeUserType';
import { useLazyGetUserProfileDetailsQuery } from '@/store/userProfile/userProfileSlice';

import styles from './ChatBot.module.scss';
import type {
  LicenseType,
  ChatbotResponse,
  ChatMessage,
  SpeechRecognition,
} from './types';
import ChatbotLoader from '../ChatbotLoader/ChatbotLoader';
import FlagTab from '../FlagTab/FlagTab';
import { AccessPrivileges } from '../Header/types';
import { useHideChatbot } from '../Header/utils';
import { StatusMessage } from '../StatusMessage/StatusMessage';

const ChatBot: FC = (): JSX.Element => {
  const [promptValue, setPromptValue] = useState<string>('');
  const [fiaChatHistory, setFiaChatHistory] = useState<ChatMessage[]>([]);
  const [superAutomationChatHistory, setSuperAutomationChatHistory] = useState<
    ChatMessage[]
  >([]);
  const License_TYPE: LicenseType = getStoreValue('test-optimize-user') || {};
  const { name } = License_TYPE.responseObject || {};
  const [chatbot_message] = useChatbotMutation();
  const [clear_chatbot] = useClearChatbotMutation();
  const [getChatbot] = useLazyGetChatbotQuery();
  const username = getStoreValue(
    'test-optimize-user',
  ) as Test_Optimize_ResponseObject;
  const { id } = username?.responseObject ?? '';
  const [getUserProfileData] = useLazyGetUserProfileDetailsQuery();
  const chatHistoryRef = useRef<HTMLDivElement>(null);
  const [windowHeight, setWindowHeight] = useState<number>(window.innerHeight);
  const wsRef = useRef<WebSocket | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const chatModalRef = useRef<{
    closeModal: () => void;
  }>(null);
  const promptInputRef = useRef<HTMLInputElement>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [aiEnabled, setAiEnabled] = useState(false);
  const currentUser = (
    getStoreValue('test-optimize-user') as {
      responseObject?: { id: string; currentLicenseId: string };
    }
  )?.responseObject;
  const profileData = useSelector(
    (state: RootState) => state.profileDataSlice.profileData,
  );
  const userId = currentUser?.id;
  const licId = currentUser?.currentLicenseId;

  const [activeTab, setActiveTab] = useState<
    'default' | 'superAutomation' | 'fiaQueries'
  >(aiEnabled ? 'default' : 'fiaQueries');

  const [isListening, setIsListening] = useState(false);
  const [additionalIcon, setAdditionalIcon] = useState('mic');
  const [sessionId, setSessionId] = useState<string>('');
  const hasFetchedChatHistoryRef = useRef(false);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const isListeningRef = useRef(isListening);
  const latestTranscriptRef = useRef<string>('');

  const chatHistory =
    activeTab === 'fiaQueries'
      ? fiaChatHistory
      : activeTab === 'superAutomation'
        ? superAutomationChatHistory
        : [];

  const { isIconVisible, isModalOpen, openModal, closeModal } =
    useHideChatbot();

  useEffect(() => {
    if (checkEmpty(profileData)) {
      getUserProfileData(id);
    }
  }, []);

  useEffect(() => {
    if (!userId || !licId || hasFetchedChatHistoryRef.current) return;

    hasFetchedChatHistoryRef.current = true;

    const fetchChatHistory = async () => {
      try {
        const response = await getChatbot({
          licenseId: licId,
          userId,
        }).unwrap();

        if (response?.responseObject?.length) {
          const chatHistory = response?.responseObject?.flatMap(history => [
            { message: history?.query, sender: 'user' as const },
            { message: history?.response, sender: 'bot' as const },
          ]);
          const latestSessionId =
            response?.responseObject[response?.responseObject?.length - 1]
              ?.session_id;

          setFiaChatHistory(chatHistory);

          if (latestSessionId) {
            setSessionId(latestSessionId);
          }
        }
      } catch (error) {
        console.error('Error fetching chatbot history.', error);
      }
    };

    fetchChatHistory();
  }, [getChatbot, licId, userId]);

  useEffect(() => {
    isListeningRef.current = isListening;
  }, [isListening]);

  useEffect(() => {
    if (isListening) {
      promptInputRef.current?.focus();
    }
  }, [isListening]);

  useEffect(() => {
    if (!aiEnabled) return;
    setActiveTab('default');
    const appHostName = window.location.hostname;
    const environment = import.meta.env.VITE_ENVIRONMENT;
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      console.info('AI Super Automation Websocket already open'); //need for debugging in future
      return;
    }
    wsRef.current = new WebSocket(
      `wss://${appHostName}/superautomation/superautomation?sessionId=${sessionId}&userId=${userId}&env=${environment}&lic=${licId}`,
    );

    wsRef.current.onopen = () => {
      console.info('Super Automation WebSocket connected'); //need for debugging in future
    };

    wsRef.current.onerror = err => {
      console.error('Super Automation WebSocket error:', err); //need for debugging in future
    };

    wsRef.current.onclose = () => {
      console.info('Super Automation WebSocket closed'); //need for debugging in future
    };

    wsRef.current.onmessage = event => {
      try {
        const data = JSON.parse(event?.data);

        if (
          data?.type === 'error' &&
          data?.message === 'Invalid action or text'
        ) {
          return;
        }

        const content = data?.content || data?.message?.message || '';

        setSuperAutomationChatHistory(prev => {
          if (
            data?.type === 'status' &&
            data?.message?.message === 'Processing...'
          ) {
            setIsProcessing(true);
            return [...prev, { sender: 'bot', isStatus: true }];
          }
          if (prev.length > 0 && prev[prev.length - 1]?.isStatus) {
            setIsProcessing(false);
            return [
              ...prev.slice(0, -1),
              {
                message: content,
                sender: data?.role == 'user' ? 'user' : 'bot',
              },
            ];
          }

          return [
            ...prev,
            { message: content, sender: data?.role == 'user' ? 'user' : 'bot' },
          ];
        });
      } catch (err) {
        console.error('WebSocket message parse error:', err);
        setIsProcessing(false);
      }
    };

    const heartbeatInterval = setInterval(() => {
      if (wsRef?.current?.readyState === WebSocket.OPEN) {
        wsRef?.current?.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);

    return () => {
      clearInterval(heartbeatInterval);
      wsRef?.current?.close();
    };
  }, [aiEnabled, modalVisible]);

  useEffect(() => {
    if (chatHistoryRef?.current) {
      chatHistoryRef?.current.scrollTo({
        top: chatHistoryRef?.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [chatHistory, modalVisible]);

  useEffect(() => {
    const handleResize = () => {
      setWindowHeight(window.innerHeight);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleCloseAction = () => {
    chatModalRef?.current?.closeModal();
  };

  const sendFiaMessage = async (message: string) => {
    setFiaChatHistory(prev => [
      ...prev,
      { message, sender: 'user' },
      { message: '', sender: 'bot', isLoading: true },
    ]);
    setPromptValue('');
    try {
      const payLoad = {
        message,
        user_id: userId || '',
        license_id: licId ?? '',
        session_id: sessionId ?? '',
      };
      const response = (await chatbot_message(
        payLoad,
      ).unwrap()) as ChatbotResponse;
      if (response) {
        setFiaChatHistory(prev =>
          prev.map((chat, index) =>
            index === prev?.length - 1
              ? { ...chat, message: response?.response, isLoading: false }
              : chat,
          ),
        );
        setPromptValue('');
        setSessionId(response?.session_id);
      }
    } catch (error) {
      console.error('Error fetching chatbot response.', error);
      setFiaChatHistory(prev =>
        prev.map((chat, index) =>
          index === prev.length - 1
            ? { ...chat, message: 'Something went wrong.', isLoading: false }
            : chat,
        ),
      );
    }
  };

  const sendSuperAutomationMessage = (message: string) => {
    if (!wsRef?.current || wsRef?.current.readyState !== WebSocket.OPEN) {
      console.warn('WebSocket not connected.');
      return;
    }

    setSuperAutomationChatHistory(prev => [
      ...prev,
      { message: message, sender: 'user' },
    ]);

    const payload = {
      action: 'query',
      text: message,
      auth: getStoreValue(CONFIG.AUTH.TOKEN_KEY),
    };

    wsRef?.current?.send(JSON.stringify(payload));
    setPromptValue('');
  };

  const handleMessageBoxClick = (content: string) => {
    setPromptValue(content);
    handlePromptSubmit(content);
  };
  const handlePromptSubmit = (value: string) => {
    const finalPrompt = typeof value === 'string' ? value : promptValue;
    if (isProcessing) return;
    if (finalPrompt?.trim() === '') return;

    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      setAdditionalIcon('mic');
    }

    if (activeTab === 'default') {
      setActiveTab('superAutomation');
    }

    if (!aiEnabled || activeTab === 'fiaQueries') {
      sendFiaMessage(finalPrompt);
    } else if (activeTab === 'superAutomation' || activeTab === 'default') {
      sendSuperAutomationMessage(finalPrompt);
    }
  };

  const handleChatInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (isProcessing) return;
    setPromptValue(e?.target?.value);
  };

  const parseHtmlString = (htmlString?: string): JSX.Element[] => {
    if (!htmlString) return [];
    const htmlTagRegex = /<\/?([a-zA-Z0-9]+)[^>]*>/g;
    const parsedContent: JSX.Element[] = [];
    let lastIndex = 0;
    htmlString = htmlString.replace(/\n/g, '<br />');
    htmlString.replace(htmlTagRegex, (match, tag, offset) => {
      const textBeforeTag = htmlString.slice(lastIndex, offset);
      if (textBeforeTag.trim()) {
        parsedContent.push(<span key={lastIndex}>{textBeforeTag}</span>);
      }
      switch (tag.toLowerCase()) {
        case 'h3': {
          if (!match.startsWith('</')) {
            const closeTagIndex = htmlString.indexOf(`</${tag}>`, offset);
            if (closeTagIndex !== -1) {
              const content = htmlString.slice(
                offset + match.length,
                closeTagIndex,
              );
              parsedContent.push(<h3 key={offset}>{content}</h3>);
              lastIndex = closeTagIndex + `</${tag}>`.length;
              return match;
            }
          }
          break;
        }
        case 'ol': {
          if (!match.startsWith('</')) {
            const closeTagIndex = htmlString.indexOf(`</${tag}>`, offset);
            if (closeTagIndex !== -1) {
              const listContent = htmlString.slice(
                offset + match.length,
                closeTagIndex,
              );
              const listItems = listContent
                .split(/<\/?li>/)
                .filter(item => item.trim())
                .map((item, index) => (
                  <li key={`${offset}-${index}`}>{item}</li>
                ));
              parsedContent.push(<ol key={offset}>{listItems}</ol>);
              lastIndex = closeTagIndex + `</${tag}>`.length;
              return match;
            }
          }
          break;
        }
        case 'br': {
          parsedContent.push(<br key={offset} />);
          break;
        }
        default: {
          console.warn(`Unhandled tag: ${tag}`);
          break;
        }
      }
      lastIndex = offset + match.length;
      return match;
    });
    const remainingText = htmlString.slice(lastIndex);
    if (remainingText.trim()) {
      parsedContent.push(<span key={lastIndex}>{remainingText}</span>);
    }
    return parsedContent;
  };
  const handleClearChat = async () => {
    setActiveTab(aiEnabled ? 'default' : 'fiaQueries');
    setFiaChatHistory([]);
    setSuperAutomationChatHistory([]);
    setPromptValue('');
    setSessionId('');
    if (
      aiEnabled &&
      wsRef?.current &&
      wsRef?.current?.readyState === WebSocket.OPEN
    ) {
      wsRef?.current?.send(JSON.stringify({ action: 'clear' }));
      console.info('Websocket session cleared'); //need for debugging in future
    } else {
      await clear_chatbot({
        licenseId: licId ?? '',
        userId: userId ?? '',
        sessionId: sessionId ?? '',
      }).unwrap();
    }
  };

  const startVoiceRecognition = () => {
    const SpeechRecognition =
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      console.error('Speech Recognition not supported in this browser');
      return;
    }

    if (!recognitionRef.current) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current!.continuous = true;
      recognitionRef.current!.interimResults = true;
      recognitionRef.current!.lang = 'en-US';

      recognitionRef.current!.onresult = (event: any) => {
        let interimTranscript = '';
        let finalTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;

          if (event.results[i].isFinal) {
            finalTranscript += ' ' + transcript.trim();
          } else {
            interimTranscript += transcript;
          }
        }

        const combined = finalTranscript + ' ' + interimTranscript;

        if (finalTranscript.length > 0) {
          setPromptValue((prev: any) => prev + finalTranscript);
        }

        latestTranscriptRef.current = finalTranscript;
      };

      recognitionRef.current!.onerror = (err: any) => {
        console.error('Speech recognition error:', err);

        setAdditionalIcon('mic');
        setIsListening(false);
      };

      recognitionRef.current!.onend = () => {
        if (isListeningRef.current) {
          setIsListening(false);
          setAdditionalIcon('mic');
        } else {
          setAdditionalIcon('mic');
          setIsListening(false);
        }
      };
    }

    if (!isListening) {
      recognitionRef.current!.start();
      setIsListening(true);
      setAdditionalIcon('mic_filled');
    } else {
      recognitionRef.current!.stop();
      setIsListening(false);
      setAdditionalIcon('mic');
    }
  };

  const headerContent = (
    <div className="header-container">
      <div className={styles.header_left}>
        <Icon
          height={16}
          width={16}
          name={
            !aiEnabled
              ? 'fia_queries'
              : activeTab === 'superAutomation'
                ? 'fia_magic'
                : activeTab === 'fiaQueries'
                  ? 'fia_queries'
                  : 'fia_ai_icon'
          }
        />
        <Typography fontWeight="medium" color="var(--brand-color)">
          {!aiEnabled
            ? 'FIA Queries'
            : activeTab === 'superAutomation'
              ? 'Super Automation'
              : activeTab === 'fiaQueries'
                ? 'FIA Queries'
                : 'FIA'}
        </Typography>
      </div>
      <div className={styles.header_right}>
        {aiEnabled && activeTab !== 'default' && (
          <>
            <FlagTab
              title={
                activeTab === 'superAutomation'
                  ? 'FIA Queries'
                  : 'Super Automation'
              }
              icon={
                <Icon
                  name={
                    activeTab === 'superAutomation'
                      ? 'fia_queries'
                      : 'fia_magic'
                  }
                  width={16}
                  height={16}
                />
              }
              fontSize={12}
              borderGradient="var(--fia-header-flag-border)"
              backgroundGradient="var(--fia-header-flag-bg)"
              onClick={() =>
                setActiveTab(
                  activeTab === 'superAutomation'
                    ? 'fiaQueries'
                    : 'superAutomation',
                )
              }
            />
            <Tooltip title="Clear chat">
              <Icon
                className={styles.header_tooltip}
                height={16}
                width={16}
                name="fia_clear"
                color="var(--brand-color)"
                onClick={handleClearChat}
              />
            </Tooltip>
          </>
        )}
        {!aiEnabled && activeTab === 'fiaQueries' && (
          <Tooltip title="Clear chat">
            <Icon
              className={styles.header_tooltip}
              height={16}
              width={16}
              name="fia_clear"
              color="var(--brand-color)"
              onClick={handleClearChat}
            />
          </Tooltip>
        )}
        <Tooltip title="Close">
          <Icon
            height={22}
            width={22}
            className={styles.header_tooltip}
            name="close"
            color="var(--brand-color)"
            onClick={handleCloseAction}
          />
        </Tooltip>
      </div>
    </div>
  );

  const renderBody = () => {
    if (!aiEnabled) {
      return (
        <div className={styles.prompt_container}>
          {CHATBOT_CONSTANTS.chatbot_questions.map((prompt, index) => (
            <div key={index} className={styles.prompt_item}>
              <Icon name="fia_ai_icon" height={16} width={16} />
              <MessageBox
                arrowPosition="left"
                content={`${index + 1}. ${prompt}`}
                isVisible
                maxWidth={400}
                onClick={() => handleMessageBoxClick(prompt)}
                isClickable
              />
            </div>
          ))}
        </div>
      );
    }

    if (activeTab === 'superAutomation') {
      return <></>;
    }
    if (activeTab === 'fiaQueries') {
      return (
        <div className={styles.prompt_container}>
          {CHATBOT_CONSTANTS.chatbot_questions.map((prompt, index) => (
            <div key={index} className={styles.prompt_item}>
              <Icon name="fia_ai_icon" height={16} width={16} />
              <MessageBox
                arrowPosition="left"
                content={`${index + 1}. ${prompt}`}
                isVisible
                maxWidth={400}
                onClick={() => handleMessageBoxClick(prompt)}
                isClickable
              />
            </div>
          ))}
        </div>
      );
    }
    return (
      <>
        <div className={styles.flag_tab}>
          <FlagTab
            title="Super Automation"
            icon={<Icon name="fia_magic" width={24} height={24} />}
            width={205}
            fontSize={16}
            borderGradient="var(--super-automation-flag-border)"
            backgroundGradient="var(--super-automation-flag-bg)"
            onClick={() => setActiveTab('superAutomation')}
          />

          <FlagTab
            title="FIA Queries"
            icon={<Icon name="fia_queries" width={24} height={24} />}
            width={160}
            fontSize={16}
            borderGradient="var(--fia-queries-flag-border)"
            backgroundGradient="var(--fia-queries-flag-bg)"
            onClick={() => setActiveTab('fiaQueries')}
          />
        </div>
        <div className={styles.prompt_container}>
          {CHATBOT_CONSTANTS.super_automation_queries?.map((prompt, index) => (
            <div key={index} className={styles.prompt_item}>
              <Icon name="fia_ai_icon" height={16} width={16} />
              <MessageBox
                arrowPosition="left"
                content={`${index + 1}. ${prompt}`}
                isVisible
                maxWidth={400}
                onClick={() => handleMessageBoxClick(prompt)}
                isClickable
              />
            </div>
          ))}
        </div>
      </>
    );
  };

  return (
    <ChatModalAi
      ref={chatModalRef}
      isIconVisible={isIconVisible}
      isOpen={isModalOpen}
      isVisible={isModalOpen}
      onOpen={openModal}
      onClose={closeModal}
      footerContent={
        <Prompt
          ref={promptInputRef}
          autoFocus
          borderRadius={80}
          height={40}
          iconHeight={24}
          iconName="right_arrow_filled_icon"
          iconPosition="right"
          iconWidth={24}
          placeholder={
            isListening
              ? 'Listening...'
              : CHATBOT_CONSTANTS.chat_input_placeholder
          }
          value={promptValue}
          width={428}
          onPromptChange={handleChatInputChange}
          submitPrompt={handlePromptSubmit}
          disableInput={isProcessing}
          iconColor={
            promptValue && !isProcessing
              ? 'var(--brand-color)'
              : 'var(--disable-color)'
          }
          isAdditionalIcon={true}
          additionalIconName={additionalIcon}
          additionIconToolTip={
            isListening ? 'Listening… click to stop' : 'Tap to Speak'
          }
          onAdditionalIconClick={startVoiceRecognition}
        />
      }
      iconHeight={42}
      iconName="fia_ai_icon"
      iconWidth={42}
      modalHeight={windowHeight - 123.5}
      modalPosition={{
        right: 10,
        bottom: 67,
      }}
      hoverIconPosition={{
        bottom: 21,
        right: 53,
      }}
      iconPosition={{
        bottom: 62,
        right: 68,
      }}
      modalWidth={444}
      headerContent={headerContent}
      hoverTitle={isProcessing ? 'FIA generating...' : 'Ask FIA'}
      onVisibilityChange={setModalVisible}
    >
      {!checkEmpty(chatHistory) ? (
        <div
          className={`${styles.chat_history_container} ${
            !checkEmpty(chatHistory) ? styles.visible : ''
          }`}
          ref={chatHistoryRef}
        >
          {chatHistory?.map((chat, index) => (
            <div
              key={index}
              className={
                chat.sender === 'user'
                  ? styles.user_message
                  : styles.bot_message
              }
            >
              {chat.sender === 'bot' && (
                <Icon name="fia_ai_icon" height={16} width={16} />
              )}
              {chat.sender === 'bot' && chat.isLoading ? (
                <ChatbotLoader />
              ) : (
                <div
                  className={`
                    ${chat?.sender === 'user' ? `${styles.message_box} ${styles.animate}` : ''}
                    ${chat?.sender !== 'user' ? styles.bot_message_width : ''}
                 `}
                >
                  {chat.isStatus ? (
                    <StatusMessage />
                  ) : (
                    <MessageBox
                      arrowPosition={chat.sender === 'user' ? 'right' : 'left'}
                      content={
                        <div className={styles.html_content}>
                          {activeTab === 'fiaQueries' ? (
                            <div
                              dangerouslySetInnerHTML={{
                                __html: chat?.message || '',
                              }}
                            />
                          ) : (
                            chat?.message
                          )}
                        </div>
                      }
                      isVisible
                      maxWidth={259}
                      isClickable={false}
                    />
                  )}
                </div>
              )}

              {chat.sender === 'user' && (
                <div className={styles.profile_image_container}>
                  <img
                    src={`data:image/jpeg;base64,${profileData?.responseObject?.profileImage}`}
                    alt="User Profile"
                    height={16}
                    width={16}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.chat_container}>
          <div className={styles.header_container}>
            <Icon name="fia_ai_icon" height={40} width={40} />
            <Typography
              fontSize={16}
              fontWeight="bold"
              className={styles.name_text}
            >
              Hi, {name}
            </Typography>
            <Typography
              color="var(--brand-color)"
              fontWeight="medium"
              textAlign="center"
            >
              {activeTab === 'fiaQueries'
                ? CHATBOT_CONSTANTS.fia_help_text
                : CHATBOT_CONSTANTS.sa_help_text}
            </Typography>
          </div>

          <div>{renderBody()}</div>
        </div>
      )}
    </ChatModalAi>
  );
};

export default ChatBot;
